import { Component, h, State, Method, Prop, Watch } from '@stencil/core';

export interface UiCmsFaqFeedbackBody {
  requestUrlFeedback: string;
  apiKey: string;
  apiVersion: string;
  uuid: string;
  faq: string;
  url: string;
}

@Component({
  tag: 'ui-cms-faq-feedback',
  styleUrl: 'ui-cms-faq-feedback.scss',
  shadow: true,
})
export class UiCmsFaqFeedback {
  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop({ mutable: true }) body: UiCmsFaqFeedbackBody = {} as any;

  @Prop({ mutable: true }) i18n = {
    confirmCancelHeadline: 'Bearbeitung abbrechen?',
    confirmCancelText:
      'Möchten Sie die Bearbeitung des Formulars wirklich abbrechen? Ihre Daten werden nicht gespeichert.',
    confirmCancelButtonCancel: 'Abbrechen',
    confirmCancelButtonContinue: 'Bearbeitung fortsetzen',
    thankYouHeadline: 'Vielen Dank für Ihr Feedback!',
    thankYouText:
      'Danke, dass Sie Ihre Stimme abgegeben haben. Wir sind immer dabei unseren Service zu verbessern und freuen uns daher auf Ihre detaillierte Meinung.',
    feedbackPositiveQuestion: 'Was hat Ihnen gefallen',
    feedbackNegativeQuestion: 'Was hat Ihnen gefehlt',
    buttonCancel: 'Abbrechen',
    buttonConfirm: 'Feedback senden',
    feedbackError:
      'Ihre Nachricht konnte aus technischen Gründen nicht übermittelt werden. Bitte versuchen Sie es erneut oder nutzen Sie eine unserer anderen',
    contactOptions: 'Kontaktmöglichkeiten',
    feedbackErrorNetwork:
      'Ihr feedback konnte nicht übermittelt werden. Es besteht keine Verbindung zum Internet. Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    feedbackPlaceholder: 'Bitte sagen Sie uns Ihre Meinung',
    feedbackSuccess: 'Ihr Feedback wurde erfolgreich übermittelt.',
    successInfo: 'Vielen Dank für Ihr Feedback. Wir kümmern uns umgehend darum.',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
  }

  @State() showConfirmationContent = false;

  @State() feedbackText = '';

  @State() isRatingPositive: boolean;

  @State() requestStatus: 'none' | 'pending' | 'success' | 'networkError' | 'generalError';

  @Watch('requestStatus')
  handleRequestStatusChange(newValue: 'none' | 'pending' | 'success' | 'networkError' | 'generalError') {
    if (newValue !== 'success') return;
    // Show confirmation for 3 seconds then close the modal
    setTimeout(() => this.closeFaqFeedbackModal(), 3000);
  }

  private dialogElement: HTMLUiDialogElement;

  @Method()
  async showFaqFeebackModal(isRatingPositive: boolean): Promise<void> {
    this.isRatingPositive = isRatingPositive;
    await this.dialogElement.show();
  }

  private closeFaqFeedbackModal(): void {
    this.dialogElement.close();
  }

  private async sendFeedback() {
    this.requestStatus = 'pending';

    const body = {
      uuid: this.body.uuid,
      url: this.body.url,
      isRatingPositive: this.isRatingPositive,
      faq: this.body.faq,
      feedback: this.feedbackText,
    };

    try {
      const res = await fetch(`${this.body.requestUrlFeedback}?api-version=${this.body.apiVersion}`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.body.apiKey,
        },
      });
      if (!res.ok) {
        throw new Error();
      }
      this.requestStatus = 'success';
    } catch (err) {
      if (navigator.onLine) {
        this.requestStatus = 'generalError';
        return;
      }
      this.requestStatus = 'networkError';
    }
  }

  private onHideDialog(): void {
    if (this.requestStatus === 'success') {
      // Close without confirmation
      this.showConfirmationContent = false;
      this.requestStatus = 'none';
      this.feedbackText = '';
      return;
    }
    if (this.showConfirmationContent) {
      // Close after confirmation
      this.showConfirmationContent = false;
      this.feedbackText = '';
      return;
    }
    // Show confirmation
    this.dialogElement.show();
    this.showConfirmationContent = true;
  }

  render() {
    return (
      <ui-dialog
        onUiDialogClose={() => this.onHideDialog()}
        ref={(el) => {
          this.dialogElement = el;
        }}
        style={{ '--component-dialog-width': '600px' }}
      >
        <div class="container">
          {/* CONFIRMATION */}
          <div
            class={{
              'inner-container': true,
              hidden: !this.showConfirmationContent || this.requestStatus === 'success',
            }}
          >
            <ui-headline level={2}>{this.i18n.confirmCancelHeadline}</ui-headline>
            <ui-paragraph>{this.i18n.confirmCancelText}</ui-paragraph>
            <div class="buttons">
              <ui-button variant="secondary" onClick={() => this.closeFaqFeedbackModal()}>
                {this.i18n.confirmCancelButtonCancel}
              </ui-button>
              <ui-button
                variant="primary"
                onClick={() => {
                  this.showConfirmationContent = false;
                }}
              >
                {this.i18n.confirmCancelButtonContinue}
              </ui-button>
            </div>
          </div>
          {/* END CONFIRMATION */}
          {/* SUCCESS */}
          <div
            class={{
              hidden: this.requestStatus !== 'success',
            }}
          >
            <div class="inner-container">
              <ui-headline level={2}>{this.i18n.thankYouHeadline}</ui-headline>
              <ui-paragraph>{this.i18n.successInfo}</ui-paragraph>
            </div>
          </div>
          {/* END SUCCESS */}
          {/* FORM */}
          <div
            class={{
              'inner-container': true,
              hidden: this.showConfirmationContent || this.requestStatus === 'success',
            }}
          >
            <ui-headline level={2}>{this.i18n.thankYouHeadline}</ui-headline>
            <ui-paragraph>{this.i18n.thankYouText}</ui-paragraph>
            <ui-paragraph class="label">
              {this.isRatingPositive ? this.i18n.feedbackPositiveQuestion : this.i18n.feedbackNegativeQuestion}
            </ui-paragraph>
            <ui-textarea
              error={this.feedbackText.length > 1024}
              maxlength={1024}
              value={this.feedbackText}
              onUiInputChange={(e) => {
                this.feedbackText = e.target.value;
              }}
              placeholder={this.i18n.feedbackPlaceholder}
            ></ui-textarea>
            <div class="buttons">
              <ui-button variant="secondary" onClick={() => this.closeFaqFeedbackModal()}>
                {this.i18n.buttonCancel}
              </ui-button>
              <ui-button
                icon="send-e-mail"
                disabled={
                  this.feedbackText.length > 1024 || this.requestStatus === 'pending' || this.feedbackText.length === 0
                }
                variant="primary"
                onClick={() => this.sendFeedback()}
              >
                {this.i18n.buttonConfirm}
              </ui-button>
            </div>
          </div>
        </div>
        {/* END FORM */}
        {/* FEEDBACK */}
        {(this.requestStatus === 'generalError' || this.requestStatus === 'networkError') && (
          <div class="sticky">
            <ui-alert variant="danger">
              {this.requestStatus === 'generalError' ? this.i18n.feedbackError : this.i18n.feedbackErrorNetwork}
            </ui-alert>
          </div>
        )}
        {this.requestStatus === 'success' && (
          <div class="sticky">
            <ui-alert variant="success">{this.i18n.feedbackSuccess}</ui-alert>
          </div>
        )}
        {/* END FEEDBACK */}
      </ui-dialog>
    );
  }
}
